/** Small pure helpers around a `.glb`: client-side pre-checks, a header summary, placement maths. */
import { MODEL_MAX_BYTES } from "@divespots/contracts";

/** "glTF" as little-endian uint32 at the start of every binary glTF. */
export const GLB_MAGIC = 0x46546c67;

export type ModelPrecheck =
  { ok: true } | { ok: false; reason: "extension" | "empty" | "size" | "magic" };

/** Quick refusals before any upload; the server's validation stays authoritative. */
export function precheckModelFile(
  file: { name: string; size: number },
  head: ArrayBuffer,
): ModelPrecheck {
  if (!/\.glb$/i.test(file.name)) return { ok: false, reason: "extension" };
  if (file.size === 0) return { ok: false, reason: "empty" };
  if (file.size > MODEL_MAX_BYTES) return { ok: false, reason: "size" };
  if (head.byteLength < 12 || new DataView(head).getUint32(0, true) !== GLB_MAGIC) {
    return { ok: false, reason: "magic" };
  }
  return { ok: true };
}

export interface GlbSummary {
  triangles: number;
  boundsM: { x: number; y: number; z: number };
  animations: string[];
}

interface Accessor {
  count?: number;
  min?: number[];
  max?: number[];
}
interface GltfJson {
  accessors?: Accessor[];
  meshes?: { primitives?: { attributes?: { POSITION?: number }; indices?: number }[] }[];
  animations?: { name?: string }[];
}

/**
 * A best-effort reading of the JSON chunk (triangle count, POSITION bounds, clip names), never of
 * the geometry. Used by the in-memory fixtures API only; the real server does the real validation.
 */
export function glbSummary(buf: ArrayBuffer): GlbSummary {
  const empty = { triangles: 0, boundsM: { x: 0, y: 0, z: 0 }, animations: [] as string[] };
  try {
    const dv = new DataView(buf);
    if (dv.getUint32(0, true) !== GLB_MAGIC) return empty;
    const jsonLen = dv.getUint32(12, true);
    const json = JSON.parse(new TextDecoder().decode(new Uint8Array(buf, 20, jsonLen))) as GltfJson;
    const acc = json.accessors ?? [];
    let triangles = 0;
    const lo = [Infinity, Infinity, Infinity];
    const hi = [-Infinity, -Infinity, -Infinity];
    for (const mesh of json.meshes ?? []) {
      for (const p of mesh.primitives ?? []) {
        const pos = p.attributes?.POSITION === undefined ? undefined : acc[p.attributes.POSITION];
        const idx = p.indices === undefined ? undefined : acc[p.indices];
        triangles += Math.floor(((idx ? idx.count : pos?.count) ?? 0) / 3);
        if (pos?.min && pos.max) {
          for (let i = 0; i < 3; i++) {
            lo[i] = Math.min(lo[i] as number, pos.min[i] as number);
            hi[i] = Math.max(hi[i] as number, pos.max[i] as number);
          }
        }
      }
    }
    const size = (i: number) =>
      Number.isFinite(lo[i]) ? (hi[i] as number) - (lo[i] as number) : 0;
    return {
      triangles,
      boundsM: { x: size(0), y: size(1), z: size(2) },
      animations: (json.animations ?? []).map((a, i) => a.name ?? `animation ${i + 1}`),
    };
  } catch {
    return empty;
  }
}

/**
 * Yaw (rotation about +Y, scene axes x east, y up, z south) that turns the model's front (+Z) to
 * the compass heading `headingDeg` (clockwise from north): front direction = (sin h, 0, -cos h).
 */
export const modelYawRad = (headingDeg: number): number => Math.PI - (headingDeg * Math.PI) / 180;

export const formatBytes = (n: number): string =>
  n >= 1024 * 1024
    ? `${(n / 1024 / 1024).toFixed(1)} MB`
    : `${Math.max(1, Math.round(n / 1024))} kB`;
